import { Heading, Modal, Page } from '@zillow/constellation-10';
import { UserStatusType } from '../../graphql/queries';
import type { UserStatus } from '../../graphql/queries';
import { useGetReportsForApplication } from '../../hooks/useGetReports';
import { TallHeightLoadingMask } from '../../styles/css-components';
import { BackgroundReport } from './BackgroundReport';
import {
  getBackgroundReportExpirationDate,
  getBackgroundReportRequestedDate,
  getCreditReportExpirationDate,
  getCreditReportRequestedDate,
} from '../../utils/user-statuses';
import { CreditReport } from './CreditReport';
import { useRouter } from 'next/router';
import { formatDate } from '../../utils/formatters';
import type { RetrieveReportsResponse } from '../../models';
import { transformReportData } from '../../utils/reports/report-utils';

interface ReportModalLoaderProps {
  reportType: string | undefined;
  userStatuses: Array<UserStatus>;
}

export function ReportModalLoader({ userStatuses, reportType }: ReportModalLoaderProps) {
  const router = useRouter();
  const { data } = useGetReportsForApplication({ enabled: !!reportType });
  const reportData = transformReportData(data);

  function onClose() {
    const { pathname, query } = router;
    const { reportType: _, ...newQuery } = query;
    router.push({ pathname, query: newQuery });
  }

  return (
    <ReportModal
      open={!!reportType}
      userStatuses={userStatuses}
      reportType={reportType}
      reportData={reportData}
      onClose={onClose}
    />
  );
}

interface ReportModalProps {
  open: boolean;
  userStatuses: Array<UserStatus>;
  reportType: string | undefined;
  reportData?: RetrieveReportsResponse;
  onClose: () => void;
}

export function ReportModal({ open, reportType, userStatuses, reportData, onClose }: ReportModalProps) {
  const backgroundExpirationDate = getBackgroundReportExpirationDate(userStatuses);
  const backgroundRequestedDate = getBackgroundReportRequestedDate(userStatuses);
  const creditExpirationDate = getCreditReportExpirationDate(userStatuses);
  const creditRequestedDate = getCreditReportRequestedDate(userStatuses);

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      size="fullScreen"
      css={{ '@media print': { position: 'absolute', padding: '0', overflowY: 'visible' } }}
      header={
        <Heading level={1}>
          {reportType === UserStatusType.backgroundReport ? 'Background check' : 'Credit report'}
        </Heading>
      }
      body={
        <TallHeightLoadingMask loading={!reportData}>
          <Page.Root css={{ backgroundColor: 'bg.neutral' }}>
            <Page.Content className="fs-exclude">
              {reportData && (
                <>
                  {reportType === UserStatusType.backgroundReport && (
                    <BackgroundReport
                      backgroundReport={reportData.backgroundReport}
                      zipCode={reportData.zipCode}
                      requestedDateTime={formatDate(backgroundRequestedDate)}
                      expirationDateTime={formatDate(backgroundExpirationDate)}
                      requiresConditionalApproval={reportData.requiresConditionalApproval}
                    />
                  )}
                  {reportType === UserStatusType.creditReport && (
                    <CreditReport
                      creditReport={reportData.creditReport}
                      requestedDateTime={formatDate(creditRequestedDate)}
                      expirationDateTime={formatDate(creditExpirationDate)}
                    />
                  )}
                </>
              )}
            </Page.Content>
          </Page.Root>
        </TallHeightLoadingMask>
      }
    />
  );
}
